import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import type { APICommonResponse } from 'src/types/common.types';

import type {
  GetFaqDetailRequest,
  GetFaqListRequest,
  GetNoticeDetailRequest,
  GetNoticeListRequest,
  GetQnaDetailRequest,
  GetQnaListRequest,
  WriteInquiryRequest,
  WriteInquiryResponse,
} from '@/store/server/common/support/support.dto';
import { supportService } from '@/store/server/common/support/support.service';

export const USE_SUPPORT_QUERY_KEY = {
  getNoticeList: 'get-notice-list',
  getNoticeDetail: 'get-notice-detail',
  getFaqList: 'get-faq-list',
  getFaqDetail: 'get-faq-detail',
  getQnaList: 'get-qna-list',
  getQnaDetail: 'get-qna-detail',
};

// 공지사항 목록 조회
interface GetNoticeListParams {
  page: GetNoticeListRequest['page'];
  size: GetNoticeListRequest['size'];
  bbsType: GetNoticeListRequest['bbs_type'];
  bbsDiv: GetNoticeListRequest['bbs_div'];
  searchText: GetNoticeListRequest['search_text'];
  sort: GetNoticeListRequest['sort'];
}

export const useGetNoticeListQuery = (params: GetNoticeListParams) => {
  return useQuery({
    queryKey: [USE_SUPPORT_QUERY_KEY.getNoticeList, params],
    queryFn: () =>
      supportService.getNoticeList({
        page: params.page,
        size: params.size,
        bbs_type: params.bbsType,
        bbs_div: params.bbsDiv,
        search_text: params.searchText,
        sort: params.sort,
      }),
    select: (res) => {
      const { data } = res.data;
      return {
        count: data.count,
        rows: data.rows.map((v) => ({
          bbsSeq: v.bbs_seq,
          bbsDiv: v.bbs_div,
          subject: v.subject,
          hits: v.hits,
          fixYN: v.fix_yn,
          regDateTime: v.reg_dttm,
        })),
      };
    },
  });
};

// 공지사항 상세 조회
interface GetNoticeDetailParams {
  bbsSeq: GetNoticeDetailRequest['bbs_seq'];
}

export const useGetNoticeDetailQuery = (params: GetNoticeDetailParams) => {
  return useQuery({
    queryKey: [USE_SUPPORT_QUERY_KEY.getNoticeDetail, params],
    queryFn: () =>
      supportService.getNoticeDetail({
        bbs_seq: params.bbsSeq,
      }),
    select: (res) => {
      const { data } = res.data;
      return {
        bbsSeq: data.bbs_seq,
        bbsDiv: data.bbs_div,
        subject: data.subject,
        hits: data.hits,
        fixYN: data.fix_yn,
        privateYN: data.private_yn,
        contents: data.contents,
      };
    },
  });
};

// FAQ 목록 조회
interface GetFaqListParams {
  page: GetFaqListRequest['page'];
  size: GetFaqListRequest['size'];
  bbsType: GetFaqListRequest['bbs_type'];
  bbsDiv: GetFaqListRequest['bbs_div'];
  searchText: GetFaqListRequest['search_text'];
  sort: GetFaqListRequest['sort'];
}

export const useGetFaqListQuery = (params: GetFaqListParams) => {
  return useQuery({
    queryKey: [USE_SUPPORT_QUERY_KEY.getFaqList, params],
    queryFn: () =>
      supportService.getFaqList({
        page: params.page,
        size: params.size,
        bbs_type: params.bbsType,
        bbs_div: params.bbsDiv,
        search_text: params.searchText,
        sort: params.sort,
      }),
    select: (res) => {
      const { data } = res.data;
      return {
        count: data.count,
        rows: data.rows.map((v) => ({
          bbsSeq: v.bbs_seq,
          bbsDiv: v.bbs_div,
          subject: v.subject,
          hits: v.hits,
          fixYN: v.fix_yn,
          regDateTime: v.reg_dttm,
        })),
      };
    },
  });
};

// FAQ 상세 조회
interface GetFaqDetailParams {
  bbsSeq: GetFaqDetailRequest['bbs_seq'];
}

export const useGetFaqDetailQuery = (params: GetFaqDetailParams) => {
  return useQuery({
    queryKey: [USE_SUPPORT_QUERY_KEY.getFaqDetail, params],
    queryFn: () =>
      supportService.getFaqDetail({
        bbs_seq: params.bbsSeq,
      }),
    select: (res) => {
      const { data } = res.data;
      return {
        bbsSeq: data.bbs_seq,
        bbsDiv: data.bbs_div,
        subject: data.subject,
        hits: data.hits,
        fixYN: data.fix_yn,
        privateYN: data.private_yn,
        contents: data.contents,
      };
    },
  });
};

// QNA 목록 조회
interface GetQnaListParams {
  page: GetQnaListRequest['page'];
  size: GetQnaListRequest['size'];
  bbsType: GetQnaListRequest['bbs_type'];
  bbsDiv: GetQnaListRequest['bbs_div'];
  replyYN?: GetQnaListRequest['reply_yn'];
  searchText: GetQnaListRequest['search_text'];
  sort?: GetQnaListRequest['sort'];
}

export const useGetQnaListQuery = (params: GetQnaListParams, { enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: [USE_SUPPORT_QUERY_KEY.getQnaList, params],
    enabled,
    queryFn: () =>
      supportService.getQnaList({
        page: params.page,
        size: params.size,
        bbs_type: params.bbsType,
        bbs_div: params.bbsDiv,
        reply_yn: params.replyYN,
        search_text: params.searchText,
        sort: params.sort,
      }),
    select: (res) => {
      const { data } = res.data;
      return {
        count: data.count,
        rows: data.rows.map((v) => ({
          bbsSeq: v.bbs_seq,
          upBbsSeq: v.up_bbs_seq,
          bbsDiv: v.bbs_div,
          subject: v.subject,
          regDateTime: v.reg_dttm,
          replyYn: v.replyYn,
        })),
      };
    },
  });
};

// QNA 목록 조회(페이지네이션 적용)
export const useGetQnaListInfiniteQuery = (params: GetQnaListParams) => {
  return useInfiniteQuery({
    queryKey: [USE_SUPPORT_QUERY_KEY.getQnaList, params],
    queryFn: ({ pageParam }) =>
      supportService.getQnaList({
        page: pageParam as number,
        size: params.size,
        bbs_type: params.bbsType,
        bbs_div: params.bbsDiv,
        reply_yn: params.replyYN,
        search_text: params.searchText,
        sort: params.sort,
      }),
    initialPageParam: params.page,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.data.data.count > params.size * pages.length) {
        return pages.length + 1;
      }

      return undefined;
    },
    select: (data) => {
      const { pages, pageParams } = data;
      return {
        pages: pages.map((page) => {
          const { data } = page.data;
          return data.rows.map((v) => ({
            bbsSeq: v.bbs_seq,
            upBbsSeq: v.up_bbs_seq,
            bbsDiv: v.bbs_div,
            subject: v.subject,
            regDateTime: v.reg_dttm,
            replyYn: v.replyYn,
          }));
        }),
        pageParams: {
          ...pageParams,
        },
      };
    },
  });
};

// QNA 상세 조회
interface GetQnaDetailParams {
  bbsSeq: GetQnaDetailRequest['bbs_seq'];
}

export const useGetQnaDetailQuery = (params: GetQnaDetailParams) => {
  return useQuery({
    queryKey: [USE_SUPPORT_QUERY_KEY.getQnaDetail, params],
    queryFn: () =>
      supportService.getQnaDetail({
        bbs_seq: params.bbsSeq,
      }),
    select: (res) => {
      const { data } = res.data;
      return {
        bbsSeq: data.bbs_seq,
        upBbsSeq: data.up_bbs_seq,
        bbsDiv: data.bbs_div,
        subject: data.subject,
        contents: data.contents,
        bbsFile: data.bbs_file,
        ansBbsSeq: data.ans_bbs_seq,
        answer: data.answer,
        ansDateTime: data.ans_dttm,
        ansBbsFile: data.ans_bbs_file,
      };
    },
  });
};

// 1:1 문의 작성
interface WriteInquiryParams {
  category: WriteInquiryRequest['bbs_div'];
  subject: WriteInquiryRequest['subject'];
  contents: WriteInquiryRequest['contents'];
  pushSms: WriteInquiryRequest['push_sms'];
  pushEmail: WriteInquiryRequest['push_email'];
  bbsFile: WriteInquiryRequest['bbs_file'];
}

export const useWriteInquiryMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess: (data: APICommonResponse<WriteInquiryResponse>) => void;
  onError: (e: unknown) => void;
}) => {
  return useMutation({
    mutationFn: (params: WriteInquiryParams) => {
      return supportService.writeInquiry({
        bbs_div: params.category,
        subject: params.subject,
        contents: params.contents,
        push_sms: params.pushSms,
        push_email: params.pushEmail,
        bbs_file: params.bbsFile,
      });
    },
    onSuccess,
    onError,
  });
};
